<template>
  <div class="expert-tool-sports-goal">
    <v-expansion-panels
      class="mb-6 mt-8"
      v-model="expandedPanel"
    >
      <v-expansion-panel
        v-for="(item, index) in boxes"
        :key="`${item.name}-${index}`"
        :disabled="item.component === 'recommendation-form'"
        :class="[
          item.component === 'recommendation-form' ? getRecommendationClass(item) : '',
          setBorderHighlight ? 'highlightedBorder' : ''
        ]"
      >
        <div
          class="expert-tool-sports-goal__box-color-mark"
          :style="{ backgroundColor: item.color }"
        ></div>
        <v-expansion-panel-header
          v-if="item.component === 'recommendation-form'"
          :class="[getPrevRecommendationClass(item), setHighlight ? 'highlighted' : '']"
          v-disable-all="hasReadOnlyAccess"
        >
          <template v-slot:default="{ open }">
            <div
              class="expert-tool-sports-goal__box-name"
              :style="{ color: item.color }"
            >{{ item.name }}</div>
            <recommendation-header
              v-if="isRecommendationReady(item)"
              :index="index"
              :isOpen="open"
              :isPrevious="item.props && item.props.isPrevious"
              @open="openRecommendation"
              @close="closeRecommendation"
              @undo="revertRecommendationChanges"
            ></recommendation-header>
            <div
              v-else
              class="expert-tool-sports-goal__placeholder"
            ></div>
          </template>
        </v-expansion-panel-header>
        <v-expansion-panel-header v-else>
          <div
            class="expert-tool-sports-goal__box-name"
            :style="{ color: item.color }"
          >{{ item.name }}</div>
          {{ item.message }}
          <div class="expert-tool-sports-goal__selected-items">
            <v-chip
              v-for="(ruleName, j) in selectedRuleNamesByType[item.props.ruleType]"
              :key="`${ruleName}-${j}`"
              class="expert-tool-sports-goal__chip"
            >{{ $ucfirst($t(ruleName, { catalog: 'app_dashboard'})) }}</v-chip>
          </div>
        </v-expansion-panel-header>
        <v-expansion-panel-content v-disable-all="hasReadOnlyAccess">
          <component
            :is="item.component"
            v-bind="item.props"
          ></component>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>
    <unsaved-changes-warning ref="unsavedChangesWarning"></unsaved-changes-warning>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

import Anomalies from './SportsGoalBoxes/Anomalies';
import Medication from './SportsGoalBoxes/Medication';
import RecommendationForm from './SportsGoalBoxes/RecommendationForm';
import RecommendationHeader from './SportsGoalBoxes/RecommendationHeader';
import PrescriptionRulesTable from './SportsGoalBoxes/PrescriptionRulesTable';
import UnsavedChangesWarning from "../../components/Parts/UnsavedChangesWarning.vue";

/**
 * Component ExpertToolSportsGoal.vue
 *
 * @module ExpertToolSportsGoal
 * @date
 * @author
 * @copyright UniWeb bvba 2022
 *
 * @link https://gitlab.uniweb.be/cor/4.04-caregiver-dashboard
 *
 * @coropreventionSW [SW-4.9] app/src/APP_DASHBOARD/templates/views/ExpertTool/ExpertToolSportsGoal.vue
 * @see https://docs.google.com/document/d/1iOpHH151s_ABr-DniJIc__q-GHpXJJVq/edit#heading=h.a0z8d9xd3xr7
 *
 */

export default {
  name: 'ExpertToolSportsGoal',
  components: {
    Anomalies,
    Medication,
    RecommendationForm,
    RecommendationHeader,
    PrescriptionRulesTable,
    UnsavedChangesWarning
  },
  data() {
    return {
      expandedPanel: null,
      setHighlight: false,
      setBorderHighlight: false,
    };
  },
  computed: {
    ...mapGetters({
      hasPreviousRecommendation: 'expertTool/hasPreviousRecommendation',
      hasRecommendation: 'expertTool/hasRecommendation',
      isRecommendationSaved: 'expertTool/isRecommendationSaved',
      selectedRuleNamesByType: 'expertTool/selectedRuleNamesByType',
      isRecommendationChanged: 'expertTool/isRecommendationChanged',
      indicationCategories: 'expertTool/indicationCategories',
      unsavedChanges: 'patientSummary/unsavedChanges',
      recommendationUpdatedByAlgorithm: 'expertTool/recommendationUpdatedByAlgorithm',
      hasReadOnlyAccess: 'user/hasReadOnlyAccess',
      recommendation: 'expertTool/recommendation',
      recommendationReverted: 'expertTool/recommendationReverted'
    }),
    changed() {
      return this.$store.getters['patientSummary/unsavedChanges']();
    },
    boxes() {
      const boxes = this.indicationCategories.map(indication => {
        indication.name = this.$ucfirst(this.$t(indication.name, {catalog: indication.catalog || "app_dashboard"}));
        indication.message = this.$ucfirst(this.$t(indication.message));
        return indication;
      });
      
      if (this.recommendation?.algorithmRan) {
        boxes.push({
          name: this.$ucfirst(this.$t('expertTool.recommendation.title')),
          message: '',
          color: '#069dd6',
          component: 'recommendation-form',
          key : 'recommendation'
        });
      }

      if (this.hasPreviousRecommendation) {
        boxes.push({
          name: this.$ucfirst(this.$t('expertTool.previousRecommendation.title')),
          message: '',
          color: '#069dd6',
          component: 'recommendation-form',
          props: { isPrevious: true },
          key : 'prev-recommendation',
        });
      }

      return boxes;
    },
    activeLevelOfGuidance() {
      const behaviourComponent = this.$store.state.patientSummary.behaviourComponents
        .find(item => item.key === 'STARTMOVING');
      return behaviourComponent?.levelOfGuidance;
    }
  },
  watch: {
    changed(curr) {
      curr && this.showNotification();
    },
    recommendation() {
      // highlight border each time recommendation is updated by algorithm
      if (this.recommendationUpdatedByAlgorithm && !this.recommendationReverted) {
        this.highlightCurrentRecommendation();
      }
    }
  },
  methods: {
    openRecommendation(index) {
      this.$store.state.expertTool.recommendationIsOpen = true;
      this.$store.dispatch('expertTool/checkDefaultStartRecommendationNextWeek')
      this.expandedPanel = index;
    },
    closeRecommendation(index) {
      this.$store.state.expertTool.recommendationIsOpen = false;
      this.$store.dispatch('expertTool/revertRecommendation');
      this.expandedPanel = null;
    },
    revertRecommendationChanges() {
      this.$store.dispatch('expertTool/revertRecommendation');
    },
    isRecommendationReady(recommendation) {
      const isPrevious = recommendation.props && recommendation.props.isPrevious;

      if (isPrevious && this.hasPreviousRecommendation) {
        return true;
      } else return !!(!isPrevious && (this.hasRecommendation || !this.recommendationUpdatedByAlgorithm));
    },
    getRecommendationClass(recommendation) {
      const isPrevious = recommendation.props && recommendation.props.isPrevious;
      if (!this.isRecommendationSaved && this.recommendationUpdatedByAlgorithm && !isPrevious && this.hasRecommendation) {
        return 'sports-goal__recommendation--not-confirmed';
      }
      return '';
    },
    getPrevRecommendationClass(recommendation) {
      const isPrevious = recommendation.props && recommendation.props.isPrevious;
      if (isPrevious) {
        return 'sports-goal__recommendation--previous';
      }
      return '';
    },
    highlightPreviousRecommendation() {
      this.setHighlight = true;
      setTimeout(() => {
        this.setHighlight = false;
      }, 500);
    },
    highlightCurrentRecommendation() {
      this.setBorderHighlight = true;
      setTimeout(() => {
        this.setBorderHighlight = false;
      }, 500);
    },
    showNotification() {
      // show notification if patient is not currently in LOG 2
      if (this.activeLevelOfGuidance === 2) {
        return;
      }
      this.$toasted.clear();
      this.$toasted.show(
        this.$ucfirst(
          this.$t("pa.goalSettingNoChangesIfLOGNotTwo", {
            params: {
              levelOfGuidance: '2',
            },
          })
        ), { icon: 'error', duration: 10000 }
      )
    },
    showMissingRequiredInfo() {
      if (!this.hasRecommendation) {
        return;
      }
      // open recommendation form and scroll to it
      this.openRecommendation(5);

      setTimeout(() => {
        const target = document.querySelector(".recommendation-form");
        this.$vuetify.goTo(target, { offset: 110 })
      }, 500)
    }
  },
  mounted() {
    this.$app.eventBus.$on('afterExpertToolRecommendationSaved', this.highlightPreviousRecommendation);
    this.$app.eventBus.$on('afterCloseExperToolUnableToSaveDialog', this.showMissingRequiredInfo);
  },
  unmounted() {
    this.$app.eventBus.$off('afterExpertToolRecommendationSaved', this.highlightPreviousRecommendation);
    this.$app.eventBus.$off('afterCloseExperToolUnableToSaveDialog', this.showMissingRequiredInfo);
  },
  beforeRouteLeave(to, from, next) {
    if (to.name === 'PatientExpertToolSafetyPrecautions'
      || to.name === 'PatientExpertToolHistory') {
        next();
    }
    if (this.unsavedChanges()) {
      this.$refs.unsavedChangesWarning.prepareToExit(to.name)
      return false;
    }
    next();
  }
};
</script>

<style lang="scss">
.expert-tool-sports-goal {
  .v-expansion-panels {
    padding-left: 250px;
  }

  .v-expansion-panel {
    border-bottom-width: 0 !important;
  }

  .v-expansion-panel::before {
    box-shadow: 0px 3px 1px -2px rgba(0, 0, 0, 0.2), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 1px 5px 0px rgba(0, 0, 0, 0.12) !important;
  }

  .expert-tool-sports-goal__box-name {
    position: absolute;
    left: -250px;
    top: 30px;
    max-width: 250px;
  }

  .v-expansion-panel-header {
    font-size: 1rem !important;
    font-weight: 600;
    padding: 24px;
    min-height: 76px;

    &.sports-goal__recommendation--previous {
      transition: background-color 2s ease-in;
      .theme--light.v-data-table {
        background-color: transparent;
      }
      &.highlighted {
        transition: background-color 0s;
        border-radius: 0 !important;
        background-color: #A7F7CA;
      }
    }
  }

  .expert-tool-sports-goal__box-color-mark {
    height: 100%;
    width: 10px;
    position: absolute;
    left: 0;
    z-index: 1;
    transition: border-radius 280ms cubic-bezier(0.4, 0, 0.2, 1);
  }

  .v-expansion-panel--next-active {
    .expert-tool-sports-goal__box-color-mark {
      border-bottom-left-radius: 4px;
    }
  }

  .v-expansion-panel--active + .v-expansion-panel {
    .expert-tool-sports-goal__box-color-mark {
      border-top-left-radius: 4px;
    }
  }

  .v-expansion-panel:last-child {
    .expert-tool-sports-goal__box-color-mark {
      border-bottom-left-radius: 4px;
    }
  }

  .v-expansion-panel:first-child {
    .expert-tool-sports-goal__box-color-mark {
      border-top-left-radius: 4px;
    }
  }

  .v-expansion-panel--active {
    .expert-tool-sports-goal__box-color-mark {
      border-radius: 16px 0 0 16px !important;
    }
  }

  .expert-tool-sports-goal__placeholder {
    height: 32px;
    width: 1px;
  }

  .sports-goal__recommendation--not-confirmed {
    border: 2px solid #069dd6 !important;
    border-left: none !important;
    transition: border 0.5s ease-in;

    &.v-expansion-panel-header:first-of-type {
      border-top-right-radius: 0 !important;
      border-top-left-radius: 0 !important;
    }

    &.highlightedBorder {
      border: 2px solid #ace7fc !important;
      border-left: none !important;
      transition: border 0s;
    }
  }

  .expert-tool-sports-goal__selected-items {
    margin-right: 12px;
    margin-left: 12px;
  }

  .expert-tool-sports-goal__chip {
    margin-right: 12px;
    margin-top: 6px;
    margin-bottom: 6px;
  }
}
</style>
